import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';

type AuthStatusCheckProps = {
  children: React.ReactNode;
};

/**
 * Component that checks if a user is authenticated before displaying error pages.
 * If not authenticated, redirects to login page.
 */
export default function AuthStatusCheck({ children }: AuthStatusCheckProps) {
  const { user, isLoading, isCheckingAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't redirect immediately - give time for token refresh
    if (!isLoading && !isCheckingAuth && !user) {
      // Give interceptor time for token refresh attempts
      const redirectTimeout = setTimeout(() => {
        if (!user) {
          navigate('/login', { replace: true });
        } else {
        }
      }, 3000); // Give 3 seconds for token refresh + retries (same as RequireAuth)

      return () => clearTimeout(redirectTimeout);
    }
  }, [user, navigate, isLoading, isCheckingAuth]);

  // Show nothing while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  // If not authenticated, don't render anything (will redirect)
  if (!user) {
    return null;
  }

  // If authenticated, render the children
  return <>{children}</>;
}
